/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import Radio from 'backbone.radio';

import HeaderBarView from '@/views/HeaderBarView';
import HeaderBarCustomerDetailsCellView from '@/views/HeaderBarView/HeaderBarCustomerDetailsCellView';
import DropdownCell from '@/backgrid/DropdownCell';
import HeaderBarLinkCellView from '@/views/HeaderBarView/HeaderBarLinkCellView';
import showModal from '@/util/showModal';
import ConfirmationModalView from '@/views/ConfirmationModalView';

import './OrderHeaderView.css';

const OrderHeaderView = HeaderBarView.extend({
  className: 'order-header-view header-bar-view clearfix',


  initialize() {
    const self = this;
    let columns = [
      {
        name: 'orderId',
        label: 'Order #',
        maxWidth: '10%'
      },
      {
        name: 'customerDetails',
        label: 'Customer Details',
        headerCell: HeaderBarCustomerDetailsCellView,
        customerDetails: this.model.get('customerDetails'),
        maxWidth: '24%',
        renderable: !EXTERNAL
      },
      {
        name: 'commandName',
        label: 'Command Name',
        maxWidth: '15%',
        headerCell: EXTERNAL ? null : HeaderBarLinkCellView,
        target: `#command/${this.model.get('commandId')}`
      },
      {
        name: 'dodaac',
        label: 'DODAAC',
        maxWidth: '10%'
      },
      {
        name: 'status',
        label: 'Status',
        maxWidth: '10%'
      },
      {
        name: 'orderType',
        label: 'Order Type',
        maxWidth: '17%'
      },
      {
        renderable: !this.model.get('internalUserCreate'),
        label: '',
        name: 'actions',
        headerCell: DropdownCell.extend({
          name: 'Actions',
          buttonClass: 'btn-outline-primary action-select mt-3 ms-auto me-3 col-3',
          buttonParentClass: `d-flex flex-row flex-grow-1 align-self-center`,
          optionFunction(data) {
            const options = [];
            if (self.options.showViewEditBtn) {
              if (self.options.isEditing) {
                options.push({ id: 'viewEditOrder', display: 'View Order' });
              } else {
                options.push({ id: 'viewEditOrder', display: 'Edit Order' });
              }
            }
            self.options.showReassignBtn && options.push({ id: 'reassign', display: 'Reassign' });
            self.options.showUnClaimButton && options.push({ id: 'unclaim', display: 'Unclaim' });
            self.options.showDeleteButton && options.push({ id: 'delete', display: 'Delete Draft' });
            self.options.showHideButton && options.push({ id: 'hide', display: 'Hide' });
            self.options.showPrintBtn && options.push({ id: 'print', display: 'Print' });

            this.currentActions = options;

            return options;
          },
          async onClick(e, model) {
            const action = e.target.id;
            switch (action) {
              case 'viewEditOrder': {
                const isEditing = self.options.isEditing;
                self.toggleIsEditing(e);
                this.render();
                return isEditing ? self.onClickView(e, model) : self.onClickEdit(e, false);
              }
              case 'reassign':
                self.onClickReassign(e);
                break;
              case 'unclaim': {
                self.onClickUnclaim(e);
                break;
              }
              case 'print':
                self.onClickShowPrinterView(e);
                break;
              case 'delete':
                self.onClickDeleteDraft(e);
                break;
              case 'hide':
                self.onClickHide(e);
                break;
            }
          }
        })
      }
    ];

    // Filter out columns that should not be rendered (columns without a 'renderable' attr are shown)
    columns = _.filter(columns, (column) => column.renderable !== false);

    this.options.columns = new Backbone.Collection(columns);

    this.listenTo(Radio.channel('order'), 'update:header', (key, value) => {
      this.model.set(key, value);
    });
  },

  toggleIsEditing() {
    this.options.isEditing = !this.options.isEditing;
  },

  async onClickUnclaim(e, model) {
    e.preventDefault();
    this.triggerMethod('unclaim', model);
  },

  async onClickReassign(e, model) {
    e.preventDefault();
    this.triggerMethod('reassign', model);
  },

  onClickShowPrinterView(e) {
    e.preventDefault();
    this.triggerMethod('print', true);
  },

  async onClickDeleteDraft(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to delete this order?`;

    showModal({
      title: 'Delete Order',
      view: new ConfirmationModalView({
        confirmText: 'Delete',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('delete:draft', model);
            return Promise.resolve();
          }
        })
      })
    });
  },

  onClickEdit(e) {
    e.preventDefault();
    this.triggerMethod('reload', true);
  },

  onClickView(e, model) {
    e.preventDefault();
    this.triggerMethod('reload', false);
  },

  async onClickHide(e, model) {
    e.preventDefault();
    const self = this;
    const message = `Are you sure you want to hide this order?`;

    showModal({
      title: 'Hide Order',
      view: new ConfirmationModalView({
        confirmText: 'Hide',
        model: new Backbone.Model({
          message,
          onConfirm() {
            self.triggerMethod('hide', model);
            return Promise.resolve();
          }
        })
      })
    });
  }
});

export default OrderHeaderView;
